import { MessageTypes } from '@dupe/common/constants'
import { useEffect, useState } from 'react'
import usePostMessageListener from '~/src/hooks/usePostMessageListener'
import { setExtensionStateSnapshot } from '~/src/lib/extensionStateSnapshot'

const useExtensionState = () => {
  const [extensionState, setExtensionState] = useState<{
    loading: boolean
    installed?: boolean
    version?: string
    mobileSafariExtension?: boolean
  }>({
    installed: false,
    loading: true,
  })

  const postMessageListener = async (messageType: string, data: any) => {
    switch (messageType) {
      // use both AUTH_SYNC and PING to check if extension is installed
      // for backward compatibility
      case MessageTypes.AUTH_SYNC:
      case MessageTypes.PING: {
        const isMobileSafari = data?.extension === 'dupe-safari-ios'

        setExtensionState((prev) => ({
          ...prev,
          loading: false,
          installed: true,
          mobileSafariExtension: isMobileSafari,
          ...(data?.version && {
            version: data?.version,
          }),
        }))
        break
      }
      default:
        break
    }
  }

  usePostMessageListener('useExtensionState', postMessageListener)

  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setExtensionState((prev) => ({
        ...prev,
        loading: false,
      }))
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  // Publish to the module-level snapshot so pure helpers
  // (enrichRedirectUrl / convertToRedirectUrl / buildRedirectLink) can attach
  // the `extension` query param to redirect URLs without having to thread
  // state through props. This hook is mounted app-wide by
  // `ExtensionModalProvider` in `apps/web/src/pages/_app.tsx`; if that
  // provider is ever skipped for a route, the snapshot stays empty and
  // extension attribution will silently drop on that route.
  useEffect(() => {
    // Only upgrade — never downgrade. Multiple components may mount this hook
    // (ExtensionModalProvider + RedirectPage), and a fresh instance starts with
    // {installed: false}. Writing that to the snapshot would clobber the true
    // value already published by the long-lived ExtensionModalProvider instance.
    if (extensionState.installed) {
      setExtensionStateSnapshot({
        installed: extensionState.installed,
        version: extensionState.version,
      })
    }
  }, [extensionState.installed, extensionState.version])

  return extensionState
}

export default useExtensionState
